import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

import type {
  AddFormProps,
  IntegrationDefinition,
  IntegrationLoaderContext,
  ManageFormProps,
  NormalizedConnection,
  IntegrationDirection,
  OAuthFlow,
  PlatformUrn,
} from '@mint-lib/integration-registry';
import type { QueryClient } from '@mint-lib/query-client';

import { makeOmniOAuthFlow } from './oauth.js';
import {
  fetchOmniConnections,
  omniConnectionRepository,
} from './repository.js';

const genericAddForm: LazyExoticComponent<ComponentType<AddFormProps>> = lazy(
  () => import('@mint-lib/omni-integration/add-form'),
);
const genericManageForm: LazyExoticComponent<ComponentType<ManageFormProps>> =
  lazy(() => import('@mint-lib/omni-integration/manage-form'));

export interface OmniIntegrationConfig {
  urn: PlatformUrn;
  displayName: string;
  /** Header direction chip — DSPs differ (e.g. linkedin/tiktok are one-way), so every platform states it. */
  direction: IntegrationDirection;
  /**
   * Defaults to `@mint-lib/omni-integration`. A standalone consumer package
   * (amazon/zemanta-integration) that owns its own add/manage
   * forms passes its own package name here so its translated strings live
   * under its own namespace instead of omni's.
   */
  i18nNamespace?: string;
  /** googleAds/adwords shows the Google API Services banner; everything else doesn't. */
  requiresGooglePolicyNotice?: boolean;
  /**
   * `undefined` (default) = the generic OAuth flow every non-quirky omni
   * platform shares; `null` = no OAuth capability (zemanta's basic
   * flow); a value = a quirk's own flow (amazon's region-aware one). This is
   * data, not a platform-identity branch inside shared code.
   */
  oauth?: OAuthFlow | null;
  /** Defaults to the generic add form. A quirk passes its own dedicated, separately-chunked component. */
  addForm?: LazyExoticComponent<ComponentType<AddFormProps>>;
  /** Defaults to the generic manage form. A quirk passes its own dedicated, separately-chunked component. */
  manageForm?: LazyExoticComponent<ComponentType<ManageFormProps>>;
}

async function prefetch(
  urn: PlatformUrn,
  ctx: IntegrationLoaderContext,
): Promise<{ connections?: NormalizedConnection[] }> {
  const queryClient = ctx.queryClient as QueryClient;
  return { connections: await fetchOmniConnections(queryClient, urn) };
}

/**
 * Generic omni-platform `IntegrationDefinition` factory — the "else" branch
 * of `packages/connections`' legacy per-platform dispatch. Every platform
 * this produces (directly, or via a quirk in `quirks/`) gets its own
 * `AddForm`/`ManageForm` component reference and its own `oauth` value —
 * resolved once here, as data — so no mounted component ever needs to ask
 * "which platform am I?" and branch on the answer.
 */
export function makeOmniIntegration(
  config: OmniIntegrationConfig,
): IntegrationDefinition {
  return {
    meta: {
      urn: config.urn,
      displayName: config.displayName,
      i18nNamespace: config.i18nNamespace ?? '@mint-lib/omni-integration',
      channel: 'dsp',
      direction: config.direction,
      requiresGooglePolicyNotice: config.requiresGooglePolicyNotice,
      showAdAccounts: true,
    },
    connection: {
      AddForm: config.addForm ?? genericAddForm,
      ManageForm: config.manageForm ?? genericManageForm,
      repository: omniConnectionRepository,
      oauth:
        config.oauth === null
          ? undefined
          : (config.oauth ?? makeOmniOAuthFlow(config.urn)),
      prefetch: (ctx) => prefetch(config.urn, ctx),
    },
  };
}
